import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { AppHome } from "./pages/AppHome";
import { BuilderPage } from "./pages/BuilderPage";
import { ConnectPage } from "./pages/ConnectPage";
import { ExportsPage } from "./pages/ExportsPage";
import { LandingPage } from "./pages/LandingPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PrintReportPage } from "./pages/PrintReportPage";
import { PropertyPickerPage } from "./pages/PropertyPickerPage";
import { ReportOverview } from "./pages/ReportOverview";
import { SettingsPage } from "./pages/SettingsPage";
import { SharedReportPage } from "./pages/SharedReportPage";
import { TaxonomyPage } from "./pages/TaxonomyPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { UtmHealthCheckPage } from "./pages/UtmHealthCheckPage";
import { UtmHealthPage } from "./pages/UtmHealthPage";
import { fetchBootstrap, type BootstrapResponse } from "./lib/api";
import { claimSessionToken, pushGtmEvent } from "./lib/gtm-events";
import { identifyHubSpotUser } from "./lib/hubspot-identify";

type AuthStatus =
  | { kind: "loading" }
  | { kind: "anonymous"; env: string }
  | { kind: "authenticated"; bootstrap: BootstrapResponse };

export function App() {
  const [auth, setAuth] = useState<AuthStatus>({ kind: "loading" });

  useEffect(() => {
    // M13.7 — fire app_open BEFORE bootstrap resolves so we capture
    // every page load (anonymous + authenticated alike). app_env +
    // app_version come from build-time Vite env vars (CI-injected) so
    // there's no need to wait on /api/bootstrap.
    pushGtmEvent("app_open", {
      app_env: (import.meta.env.VITE_APP_ENV as string | undefined) ?? "local",
      app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev",
    });

    fetchBootstrap()
      .then((b) => {
        if (b.authenticated && b.user) {
          identifyHubSpotUser({
            email: b.user.email,
            userId: b.user.id,
            propertyId: b.defaultProperty?.propertyId ?? null,
            propertyName: b.defaultProperty?.propertyName ?? null,
            accountName: b.defaultProperty?.accountName ?? null,
            grantMode: b.grantMode ?? null,
          });
          // M13.7 — once-per-tab profile_connected. The dedupe key is
          // per-user so re-auth as a different user in the same tab
          // still fires for the new identity (rare, but cheap to support).
          if (claimSessionToken(`ts.profile_connected.session.${b.user.id}`)) {
            pushGtmEvent("profile_connected", {
              user_id: b.user.id,
              property_id: b.defaultProperty?.propertyId ?? null,
              property_name: b.defaultProperty?.propertyName ?? null,
              account_name: b.defaultProperty?.accountName ?? null,
              grant_mode: b.grantMode ?? null,
            });
          }
        }
        setAuth(
          b.authenticated
            ? { kind: "authenticated", bootstrap: b }
            : { kind: "anonymous", env: b.env },
        );
      })
      .catch(() => setAuth({ kind: "anonymous", env: "unknown" }));
  }, []);

  useHashScroll();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/utm-health-check" element={<UtmHealthCheckPage />} />
      <Route path="/unsubscribed" element={<UnsubscribePage />} />
      <Route path="/share/:token" element={<SharedReportPage />} />
      <Route path="/app/connect" element={<ConnectPage />} />
      <Route
        path="/app/report"
        element={
          <RequireAuth auth={auth}>
            <ReportOverview />
          </RequireAuth>
        }
      />
      <Route
        path="/app/properties"
        element={
          <RequireAuth auth={auth}>
            <PropertyPickerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/settings"
        element={
          <RequireAuth auth={auth}>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/utm-health"
        element={
          <RequireAuth auth={auth}>
            <UtmHealthPage />
          </RequireAuth>
        }
      />
      {/* Builder + Taxonomy are content-only tools (URL-building math
       *  for the builder, static canonical reference for the taxonomy)
       *  and don't need GA4 auth. Letting them render for anon users
       *  means share-link recipients can use the tools without
       *  reconnecting Google. The pages handle their own optional
       *  authed-context (presets list, Save-as-preset button) — see
       *  BuilderPage. */}
      <Route path="/app/builder" element={<BuilderPage />} />
      <Route path="/app/taxonomy" element={<TaxonomyPage />} />
      <Route
        path="/app/exports"
        element={
          <RequireAuth auth={auth}>
            <ExportsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/report/print"
        element={
          <RequireAuth auth={auth}>
            <PrintReportPage />
          </RequireAuth>
        }
      />
      <Route path="/app/*" element={<AppHome auth={auth} />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function RequireAuth({ auth, children }: { auth: AuthStatus; children: ReactNode }) {
  const location = useLocation();
  if (auth.kind === "loading") return null;
  if (auth.kind === "anonymous") {
    return (
      <Navigate to={`/app/connect?return_to=${encodeURIComponent(location.pathname)}`} replace />
    );
  }
  return children;
}

// React Router does not auto-scroll on hash changes. Sidebar links use
// fragments (eg. /app/report#platforms) to jump to the corresponding
// section. The 60ms timeout lets the destination route paint before we
// look up the element — otherwise the first click after route change
// finds nothing and silently no-ops.
function useHashScroll() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);
}
